import type { ReactNode, Ref } from 'react'

import { Button } from '@/components/ui/button'
import { Kbd } from '@/components/ui/kbd'
import { SheetFooter } from '@/components/ui/sheet'

import { ResetToCareforceDialog } from '../ResetToCareforceDialog'
import { ResetVariantDialog } from '../ResetVariantDialog'
import type { FooterAside, FooterSpec } from './model'

interface Resets {
  name: string
  instanceCount: number
  variantCount: number
  isResetting: boolean
  onResetVariant: () => void
  onResetToCareforce: () => void
}

const ASIDES: Record<FooterAside, (resets: Resets) => ReactNode> = {
  resetVariant: (resets) => (
    <ResetVariantDialog
      name={resets.name}
      isResetting={resets.isResetting}
      onConfirm={resets.onResetVariant}
    />
  ),
  resetToCareforce: (resets) => (
    <ResetToCareforceDialog
      name={resets.name}
      instanceCount={resets.instanceCount}
      variantCount={resets.variantCount}
      isResetting={resets.isResetting}
      onConfirm={resets.onResetToCareforce}
    />
  ),
  careforceNote: () => (
    <p className="hidden text-xs text-muted-foreground sm:block">
      Careforce edits don't appear in your duty history
    </p>
  ),
}

/** The kind's dismiss and primary action, its aside, and unsaved state. */
export function SnippetSheetFooter({
  footer,
  resets,
  isDirty,
  isSaving,
  canSave,
  onSave,
  onCustomize,
  onDismiss,
  primaryRef,
}: {
  footer: FooterSpec | null
  resets: Resets
  isDirty: boolean
  isSaving: boolean
  canSave: boolean
  onSave: () => void
  onCustomize?: () => void
  onDismiss: () => void
  primaryRef: Ref<HTMLButtonElement>
}) {
  const primary = footer?.primary
  const onPrimary = primary?.action === 'customize' ? onCustomize : onSave
  return (
    <SheetFooter className="flex-row items-center gap-2 border-t border-neutral-subtle-border px-4 py-3">
      {footer?.aside && ASIDES[footer.aside](resets)}
      {isDirty && (
        <span className="flex items-center gap-1.5 text-xs text-warning-low-contrast">
          <span
            aria-hidden="true"
            className="size-1.5 rounded-full bg-current"
          />
          Unsaved changes
        </span>
      )}
      <span className="flex-1" />
      <Button
        type="button"
        variant="outline"
        disabled={isSaving}
        onClick={onDismiss}>
        {footer?.dismiss ?? 'Cancel'}
      </Button>
      {primary && onPrimary && (
        <Button
          ref={primaryRef}
          type="button"
          disabled={primary.action === 'save' && !canSave}
          onClick={onPrimary}>
          {isSaving ? 'Saving…' : primary.label}
          {primary.action === 'save' && (
            <Kbd
              aria-hidden="true"
              className="hidden bg-transparent text-current opacity-75 sm:inline-flex">
              ⌘↵
            </Kbd>
          )}
        </Button>
      )}
    </SheetFooter>
  )
}
